import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import Pagereview from "../../components/pageReview/Pagereview";
import HeroMain from "../../components/innerPageHero/HeroMain";
import { getFAQ, getFAQCategories } from "../../utils/apiCalls";
import { FormContext } from "../CMSRenderer/CMSRenderer";

const FAQs = React.memo(({ config }) => {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [accordionStates, setAccordionStates] = useState([]);
  const { currentLang } = useContext(FormContext);
  const [isExpand, setIsExpand] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  const [faqData, setFaqData] = useState({
    category: [],
    faq: [],
  });
  const [loading, setLoading] = useState({
    categoryLoading: true,
    questionLoading: true,
  });
  const accordionRefs = useRef([]);

  useEffect(() => {
    setLoading({
      ...loading,
      questionLoading: true,
    });
    setIsExpand(false);
    const getData = async () => {
      let faqDetailData = {};
      let faqCategories = "";
      if (loading?.categoryLoading) {
        const categoriesAPI = await getFAQCategories();
        faqCategories = categoriesAPI;
        faqDetailData = {
          category: categoriesAPI?.data || [],
        };
      }
      if (loading?.categoryLoading && faqCategories?.data?.length <= 0) {
        setLoading({
          ...loading,
          categoryLoading: false,
          questionLoading: false,
        });
        return;
      }
      const faqQuestion = await getFAQ({
        faqID:
          selectedCategory !== ""
            ? selectedCategory
            : faqCategories?.data?.[0]?.dguid,
      });
      if (selectedCategory === "") {
        setSelectedCategory(faqCategories?.data?.[0]?.dguid);
      }
      setAccordionStates(Array(faqQuestion?.data?.data.length).fill(false));
      accordionRefs.current = [];
      faqDetailData = {
        ...faqDetailData,
        faq: faqQuestion?.data?.data || [],
      };
      setFaqData({
        ...faqData,
        ...faqDetailData,
      });
      setLoading({
        ...loading,
        categoryLoading: false,
        questionLoading: false,
      });
    };

    getData();
  }, [selectedCategory]);

  useEffect(() => {
    const handleInputChanged = (e) => {
      const value = e.target?.value || e.detail?.value || "";
      setSearchQuery(value);
    };

    const handleOnChanged = (e) => {
      const value = e.target?.value || e.detail?.value || "";
      setSearchQuery(value);
    };

    const searchElement = searchInputRef.current;
    if (searchElement) {
      searchElement.addEventListener('inputChanged', handleInputChanged);
      searchElement.addEventListener('onChanged', handleOnChanged);
      
      return () => {
        searchElement.removeEventListener('inputChanged', handleInputChanged);
        searchElement.removeEventListener('onChanged', handleOnChanged);
      };
    }
  }, []);

  const filteredFAQs = useMemo(() => {
    if (!searchQuery.trim()) {
      return faqData.faq;
    }

    const searchLower = searchQuery.toLowerCase().trim();
    return faqData.faq.filter((item) => {
      const question = currentLang === "en" ? item.questionEn : item.questionAr;
      const answer = currentLang === "en" ? item.answerEn : item.answerAr;
      
      const cleanAnswer = answer.replace(/<[^>]*>/g, '');
      
      return (
        question.toLowerCase().includes(searchLower) ||
        cleanAnswer.toLowerCase().includes(searchLower)
      );
    });
  }, [faqData.faq, searchQuery, currentLang]);

  const handleChipClick = (category) => {
    setSelectedCategory(category);
    setSearchQuery("");
  };

  const handleSearchInput = (e) => {
    const value = e.target?.value || "";
    setSearchQuery(value);
  };

  const expandAll = () => {
    accordionRefs.current.forEach((ref) => {
      if (ref && typeof ref.openAccordion === "function") {
        if (isExpand === false) {
          ref.openAccordion();
          setIsExpand(true);
        } else {
          ref.closeAccordion();
          setIsExpand(false);
        }
      }
    });
  };

  return (
    <>
      <HeroMain src="24_Investment & Health Tourism -Investing in the Healthcare Sector in Dubai.jpg" />
      <h2 className="mb-4 Medium text-primary">
        {currentLang === "en"
          ? config.header
          : config.header_ar
          ? config.header_ar
          : config.header}
      </h2>
      <h5 className="md:!my-4 my-2 Regular text-onSurfaceVariant">
        {currentLang === "en"
          ? config.body
          : config.body_ar
          ? config.body_ar
          : config.body}
      </h5>

      <dda-search-input
        ref={searchInputRef}
        placeholder={
          currentLang === "en"
            ? config.search_input_placeholder
            : config.search_input_placeholder_ar
            ? config.search_input_placeholder_ar
            : config.search_input_placeholder
        }
        label={
          currentLang === "en"
            ? config.search_input_label
            : config.search_input_label_ar
            ? config.search_input_label_ar
            : config.search_input_label
        }
        size="default"
        helper_text=""
        error_message=""
        validation_id=""
        show_button="true"
        has_error="false"
        custom_class="my-4"
        component_mode=""
        button_id="button"
        input_id="Search"
        aria_label="search-input"
        button_aria_label="clear-search"
        onInput={handleSearchInput}
        search_input_name="search-input"
        close_button_name="close-button"
        search_button_name="search-button"
        button_text={
          currentLang === "en"
            ? config.search_button_text
            : config.search_button_text_ar
            ? config.search_button_text_ar
            : config.search_button_text
        }
      ></dda-search-input>
      <div className="flex gap-4 my-4 flex-wrap">
        {faqData?.category.map((category) => (
          <dda-chip
            chip_text={
              currentLang === "en"
                ? category?.captionEn
                : category?.captionAr || category.captionEn
            }
            key={category?.dguid}
            bg_color={selectedCategory === category ? "primary" : "grey"}
            size="lg"
            show_close_icon="false"
            onclick={() => handleChipClick(category?.dguid)}
            class={`hydrated cursor-pointer ${
              selectedCategory === category?.dguid ? "" : "*:bg-white"
            }`}
          ></dda-chip>
        ))}
      </div>

      <div className="flex justify-between items-center">
        <h6 className="font-medium ">
          {currentLang === "en"
            ? faqData?.category?.find((i) => i?.dguid === selectedCategory)
                ?.captionEn
            : faqData?.category?.find((i) => i?.dguid === selectedCategory)
                ?.captionAr || ""}
        </h6>
        {filteredFAQs?.length > 0 && (
          <dda-button
            button_color="default-tertiary"
            button_id="expand-all"
            aria_label="expand-all"
            onclick={expandAll}
            button_name={isExpand ? "Collapse All" : "Expand All"}
            class="hydrated"
            button_text={
              currentLang === "en"
                ? isExpand
                  ? config.collpase_section_text
                  : config.expand_section_text
                : isExpand
                ? config.collpase_section_text_ar
                : config.expand_section_text_ar
                ? isExpand
                  ? config.collpase_section_text_ar
                  : config.expand_section_text_ar
                : isExpand
                ? config.collpase_section_text
                : config.expand_section_text
            }
          ></dda-button>
        )}
      </div>

      {loading?.questionLoading ? (
        <div>Loading ...</div>
      ) : (
        (filteredFAQs?.length > 0 ? filteredFAQs : [])?.map((item, index) => (
          <dda-accordion
            key={index}
            ref={(el) => (accordionRefs.current[index] = el)}
            design="bg-border"
            header_text={
              currentLang === "en"
                ? item.questionEn
                : item.questionAr
                ? item.questionAr
                : item.questionEn
            }
            // body_description={
            //   currentLang === "en"
            //     ? item.answerEn
            //     : item.answerAr
            //     ? item.answerAr
            //     : item.answerEn
            // }
            custom_class="my-3"
            accordion_icon=""
            isOpen={accordionStates[index]}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: currentLang === "en" ? item.answerEn : item.answerAr,
              }}
            ></div>
          </dda-accordion>
        ))
      )}

      {!loading?.questionLoading && searchQuery && filteredFAQs.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {currentLang === "en" 
              ? "No FAQs found matching your search criteria." 
              : "لم يتم العثور على أسئلة شائعة تطابق معايير البحث."
            }
          </p>
        </div>
      )}
    </>
  );
});

export default FAQs;
